/**
 * The one read of the native Creative Performance page (spec §2.2): get_creative_facts (migration 123), one row per
 * creative, paged by ad_id (one row per ad, so unique). Every page or a throw: a partial list would show as a shorter
 * table nobody measured. Names are kept as stored (a NULL one reads ""); native ids, formats and currencies follow D2's
 * normalisation (R21): trimmed, and a blank one is null.
 *
 * @module services/creatives/creativeReaders
 */
import { getSupabaseClient } from '@/config';
import { entityStatusOf, textOrNull, trimmedOrNull } from '@/services/channels/channelReaders';
import { isAdChannel, type AdChannel } from '@/services/channels/channelTypes';
import { nullableNumber } from '@/services/overview/overviewMath';
import { errorMessageOf, readAllRows } from '@/services/supabasePaging';
import type { CreativeFact } from './creativeTypes';
import type { CreativeWindows } from './creativeWindows';

/** A get_creative_facts row; numeric may arrive as a string. */
export interface CreativeFactDb {
  workspace_id: string;
  platform: string;
  ad_id: string;
  external_ad_id: string | null;
  ad_name: string | null;
  ad_format: string | null;
  ad_status: string | null;
  ad_set_id: string;
  campaign_id: string;
  campaign_name: string | null;
  campaign_status: string | null;
  campaign_end_date: string | null;
  objective: string;
  account_id: string | null;
  currency: string | null;
  spend: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  video_views: number | string | null;
  reactions: number | string | null;
  comments: number | string | null;
  shares: number | string | null;
  impressions_first: number | string | null;
  clicks_first: number | string | null;
  impressions_second: number | string | null;
  clicks_second: number | string | null;
  result_spend: number | string | null;
  result_impressions: number | string | null;
  result_reach: number | string | null;
  result_clicks: number | string | null;
  result_conversions: number | string | null;
  last_date: string;
  first_impression_date: string | null;
  account_first_date: string | null;
}

/** The RPC returns the four ad channels only; anything else means the function changed: fail the read, loudly. */
const platformOf = (v: string): AdChannel => {
  if (!isAdChannel(v)) throw new Error(`creative facts read failed: unknown platform "${v}"`);
  return v;
};

export const creativeFactOf = (r: CreativeFactDb): CreativeFact => ({
  workspaceId: r.workspace_id,
  platform: platformOf(r.platform),
  adId: r.ad_id,
  externalAdId: trimmedOrNull(r.external_ad_id),
  adName: r.ad_name ?? '',
  adFormat: textOrNull(r.ad_format),
  adStatus: entityStatusOf(r.ad_status),
  adSetId: r.ad_set_id,
  campaignId: r.campaign_id,
  campaignName: r.campaign_name ?? '',
  campaignStatus: entityStatusOf(r.campaign_status),
  campaignEndDate: r.campaign_end_date,
  objective: r.objective,
  accountId: r.account_id,
  currency: trimmedOrNull(r.currency),
  spend: nullableNumber(r.spend),
  impressions: nullableNumber(r.impressions),
  clicks: nullableNumber(r.clicks),
  videoViews: nullableNumber(r.video_views),
  reactions: nullableNumber(r.reactions),
  comments: nullableNumber(r.comments),
  shares: nullableNumber(r.shares),
  impressionsFirst: nullableNumber(r.impressions_first),
  clicksFirst: nullableNumber(r.clicks_first),
  impressionsSecond: nullableNumber(r.impressions_second),
  clicksSecond: nullableNumber(r.clicks_second),
  resultSpend: nullableNumber(r.result_spend),
  resultImpressions: nullableNumber(r.result_impressions),
  resultReach: nullableNumber(r.result_reach),
  resultClicks: nullableNumber(r.result_clicks),
  resultConversions: nullableNumber(r.result_conversions),
  lastDate: r.last_date,
  firstImpressionDate: r.first_impression_date,
  accountFirstDate: r.account_first_date,
});

/** Every creative of the scope that delivered in the window; [] without a scope (no read). */
export const readCreativeFacts = async (workspaceIds: readonly string[], w: CreativeWindows): Promise<CreativeFact[]> => {
  if (workspaceIds.length === 0) return [];
  const { rows, error } = await readAllRows<CreativeFactDb>((from, to) =>
    getSupabaseClient()
      .rpc('get_creative_facts', { p_workspace_ids: [...workspaceIds], p_start: w.start, p_end: w.end, p_mid: w.mid, p_result_end: w.resultEnd })
      .order('ad_id', { ascending: true })
      .range(from, to), 'creative facts read');
  if (error) throw new Error(`creative facts read failed: ${errorMessageOf(error)}`);
  return rows.map(creativeFactOf);
};
